import {Bookmark, UserPreferences} from '@app/modules/shared/models/navigation';

/**
 * The client's copy of the server's preference rules (snoball_server
 * api/services/UserPreference.service.js, Config.navigation.limits). The server is the
 * authority and refuses what breaks them; these keep the client from asking.
 */
export const PREFERENCE_LIMITS = {
  bookmarks: 50,
  bookmarkLabelLength: 120,
  bookmarkUrlLength: 1024,
  expandedGroups: 100,
};

export const DEFAULT_PREFERENCES: UserPreferences = Object.freeze({
  menuMode: 'top',
  sidebarCollapsed: false,
  expandedGroups: [],
  bookmarks: [],
}) as UserPreferences;

const MENU_ID = /^[a-z][a-zA-Z0-9]*(?:\.[a-z][a-zA-Z0-9]*)*$/;
export const ICON_PATTERN = /^fa-(?:solid|regular|brands) fa-[a-z0-9]+(?:-[a-z0-9]+)*$/;

// Pages a bookmark must never point at: the sign-in flows, whose links carry one-time tokens,
// and the pages that exist only outside a session.
const DENIED_PATHS = ['/login', '/temporary-login', '/password', '/resetpassword', '/front',
  '/data-protection/approve', '/data-protection/evidence'];
const SENSITIVE_QUERY_KEY = /(token|secret|passw|otp|code|challenge|signature|^sig$|api[-_]?key|mfa_link|returnurl)/i;
const CONTROL_CHARACTERS = /[\u0000-\u001f\u007f]/g;

/**
 * The in-app path a page bookmark may store, or null. Pathname without a trailing slash, the
 * query minus credential-carrying keys, no fragment. Rendered only through routerLink, never
 * as an href, so even a value that slipped past here could not leave the app.
 */
export function normalizeInAppUrl(value: any): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const url = value.trim();
  if (!url || url.length > PREFERENCE_LIMITS.bookmarkUrlLength) {
    return null;
  }
  if (!url.startsWith('/') || url.startsWith('//') || url.startsWith('/\\')) {
    return null;
  }
  if (/[\\\s]/.test(url) || /[\u0000-\u001f\u007f]/.test(url)) {
    return null;
  }
  let parsed: URL;
  try {
    parsed = new URL(url, 'https://in-app.invalid');
  } catch (_) {
    return null;
  }
  if (parsed.origin !== 'https://in-app.invalid') {
    return null;
  }
  const pathname = parsed.pathname.replace(/\/+$/, '') || '/';
  if (DENIED_PATHS.some((denied) => pathname === denied || pathname.startsWith(`${denied}/`))) {
    return null;
  }
  const sensitive: string[] = [];
  parsed.searchParams.forEach((_value, key) => {
    if (SENSITIVE_QUERY_KEY.test(key)) {
      sensitive.push(key);
    }
  });
  sensitive.forEach((key) => parsed.searchParams.delete(key));
  const search = parsed.searchParams.toString();
  return pathname + (search ? `?${search}` : '');
}

export function cleanBookmarkLabel(label: any): string {
  return String(label || '').replace(CONTROL_CHARACTERS, '').replace(/\s+/g, ' ').trim()
    .slice(0, PREFERENCE_LIMITS.bookmarkLabelLength);
}

/** What a bookmark points at - the only identity a bookmark has. */
export function bookmarkKey(bookmark: Bookmark): string {
  return bookmark.kind === 'menu' ? `menu:${bookmark.menuId}` : `page:${bookmark.url}`;
}

function cleanBookmark(bookmark: any): Bookmark | null {
  if (!bookmark || typeof bookmark !== 'object') {
    return null;
  }
  if (bookmark.kind === 'menu') {
    if (typeof bookmark.menuId !== 'string' || !MENU_ID.test(bookmark.menuId)) {
      return null;
    }
    const label = cleanBookmarkLabel(bookmark.label);
    return {kind: 'menu', menuId: bookmark.menuId, ...(label ? {label} : {})};
  }
  if (bookmark.kind === 'page') {
    const url = normalizeInAppUrl(bookmark.url);
    if (!url) {
      return null;
    }
    const icon = typeof bookmark.icon === 'string' && ICON_PATTERN.test(bookmark.icon) ? bookmark.icon : null;
    return {kind: 'page', url, label: cleanBookmarkLabel(bookmark.label) || url, ...(icon ? {icon} : {})};
  }
  return null;
}

/** Preferences with every key present and nothing invalid - whatever arrived. */
export function normalizePreferences(raw: any): UserPreferences {
  if (!raw || typeof raw !== 'object') {
    return {...DEFAULT_PREFERENCES, expandedGroups: [], bookmarks: []};
  }
  const seen = new Set<string>();
  const bookmarks = (Array.isArray(raw.bookmarks) ? raw.bookmarks : [])
    .map(cleanBookmark)
    .filter((bookmark): bookmark is Bookmark => {
      if (!bookmark || seen.has(bookmarkKey(bookmark))) {
        return false;
      }
      seen.add(bookmarkKey(bookmark));
      return true;
    })
    .slice(0, PREFERENCE_LIMITS.bookmarks);
  const expandedGroups = Array.from(new Set<string>((Array.isArray(raw.expandedGroups) ? raw.expandedGroups : [])
    .filter((id: any) => typeof id === 'string' && id.length <= 100 && MENU_ID.test(id))))
    .slice(-PREFERENCE_LIMITS.expandedGroups);
  return {
    menuMode: raw.menuMode === 'side' ? 'side' : 'top',
    sidebarCollapsed: raw.sidebarCollapsed === true,
    expandedGroups,
    bookmarks,
  };
}
